import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MfaSetup, BackupCodes } from "@/components/security/MfaSetup";
import { KeyRound, Loader2, ShieldCheck, Users } from "lucide-react";

const err = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

function CodeAction({ label, testId, onSubmit, tone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => { e.preventDefault(); setBusy(true); await onSubmit(code); setBusy(false); setCode(""); };
  return (
    <form onSubmit={go} className="flex gap-2" data-testid={`${testId}-form`}>
      <Input value={code} onChange={(e) => setCode(e.target.value.trim().slice(0, 14))} placeholder="Code from your app" className="h-9 max-w-[200px] font-mono" data-testid={`${testId}-code`} />
      <Button type="submit" size="sm" variant="outline" disabled={busy || code.length < 6} className={tone} data-testid={testId}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : label}</Button>
    </form>
  );
}

function OrgRequire({ enabled }) {
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/org/security").then(({ data }) => setOrg(data)).catch(() => {}); }, []);
  if (!org?.can_edit) return null;
  const save = async (v) => {
    try { const { data } = await api.patch("/org/security", { require_2fa: v }); setOrg((o) => ({ ...o, ...data })); toast.success(v ? "2-step sign-in is now required for your team" : "2-step sign-in is now optional"); }
    catch (e) { toast.error(err(e, "Could not save")); }
  };
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border pt-4" data-testid="org-require-2fa">
      <div className="flex items-start gap-2"><Users className="h-4 w-4 text-muted-foreground mt-0.5" /><div><div className="text-sm font-medium">Require it for my whole team</div><div className="text-xs text-muted-foreground">{enabled ? "Teammates set it up at their next email sign-in." : "Turn it on for your own account first."}</div></div></div>
      <Switch checked={org.require_2fa} onCheckedChange={save} disabled={!enabled && !org.require_2fa} data-testid="org-require-2fa-switch" />
    </div>
  );
}

export function TwoStepSettings() {
  const [st, setSt] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/auth/2fa/status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st?.eligible) return null;
  const disable = async (code) => { try { await api.post("/auth/2fa/disable", { code }); toast.success("2-step sign-in turned off"); load(); } catch (e) { toast.error(err(e, "That code didn't match")); } };
  const regen = async (code) => { try { const { data } = await api.post("/auth/2fa/backup-codes", { code }); setCodes(data.backup_codes); load(); } catch (e) { toast.error(err(e, "That code didn't match")); } };
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4" data-testid="two-step-settings">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2"><KeyRound className="h-4 w-4 text-primary mt-0.5" /><div><div className="font-medium text-sm">2-step sign-in</div><div className="text-xs text-muted-foreground">A code from an authenticator app after your password. Google sign-in uses Google's own 2-step instead.</div></div></div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${st.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-muted text-muted-foreground"}`} data-testid="mfa-status">{st.enabled ? "On" : "Off"}</span>
      </div>
      {codes ? <BackupCodes codes={codes} onDone={() => setCodes(null)} /> : st.enabled ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> {st.backup_remaining} backup code{st.backup_remaining === 1 ? "" : "s"} left</p>
          <div className="text-xs text-muted-foreground">New backup codes</div>
          <CodeAction label="Get new codes" testId="mfa-regen" onSubmit={regen} />
          {!st.required && <><div className="text-xs text-muted-foreground">Turn off</div><CodeAction label="Turn off" testId="mfa-disable" onSubmit={disable} tone="text-rose-400 border-rose-500/30" /></>}
        </div>
      ) : <MfaSetup onEnabled={(d) => { setCodes(d.backup_codes); load(); toast.success("2-step sign-in is on"); }} />}
      <OrgRequire enabled={st.enabled} />
    </div>
  );
}
